/**
 * Utility to check if UI elements should be hidden for specific clients
 * This applies only when the actual client is logged in, not when admins impersonate them
 */

/**
 * Decode JWT token to check if it's an impersonation token
 * The backend sets isImpersonationToken: true and roles: ['user-impersonation'] in the JWT
 */
export const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Check if the current session is in impersonate mode
 * Impersonate mode is detected by checking the JWT token for isImpersonationToken flag
 * @param accessToken - The JWT access token from Redux store
 */
export const isImpersonateMode = (accessToken?: string): boolean => {
  if (!accessToken) return false;
  
  const decoded = decodeJWT(accessToken);
  
  // Check if the token has isImpersonationToken flag or user-impersonation role
  return decoded?.isImpersonationToken === true || 
         decoded?.roles?.includes('user-impersonation');
};

/**
 * Check if UI elements should be hidden for the current user
 * Returns true only if:
 * 1. User ID is 46301
 * 2. NOT in impersonate mode (admin viewing as this user)
 * @param userId - The user ID to check
 * @param accessToken - The JWT access token from Redux store
 */
export const shouldHideForRestrictedClient = (userId?: number, accessToken?: string): boolean => {
  if (!userId) return false;
  
  const isRestricted = userId === 46301;
  const isImpersonating = isImpersonateMode(accessToken);
  
  // Hide only if user is restricted AND not being impersonated
  return isRestricted && !isImpersonating;
};
